import React, { useState, useEffect } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Header from "../../../components/Header";
import SEO from "../../../components/SEO";
import Banner from "../../../components/Banner";
import Footer from "../../../components/Footer";
import ScrollToTopButton from "../../../components/ScrollTop";
import WhatsAppMsg from "../../../components/WhatsApp";
import ProductSpecifications from "../../../components/ProductSpecification";
import vistaProductSpecs from "../../../utils/vistaDisplayProductSpecs";
import { Helmet } from "react-helmet";

const slides = [
  {
    id: 1,
    title: "Indoor Commercial Displays for Retail Showrooms & Malls",
    altText:
      "COLTEC Vista Digital Display at a Travel Bag Retail Store for Product Promotions.",
    description:
      "Retail showrooms and shopping malls use high-resolution commercial displays for dynamic advertising and customer engagement. Ultra-HD video walls showcase promotions, product highlights, and interactive content to attract shoppers. AI-powered analytics track customer engagement, while interactive touchscreen kiosks allow personalized browsing, enhancing brand visibility, customer experience, and in-store sales.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-display-travel-bag-store.webp",
  },
  {
    id: 2,
    title: "Executive Boardrooms – High-Impact Business Presentations",
    altText:
      "COLTEC Inertia LED Video Wall for Shopping Malls – Advanced advertising and promotional displays.",
    description:
      "Executive boardrooms demand high-performance commercial displays for seamless presentations, financial discussions, and investor meetings. Ultra-HD resolution ensures sharp visuals, while wireless content sharing and multi-screen support enhance collaboration. Built-in video conferencing compatibility with Zoom and Microsoft Teams facilitates hybrid meetings, making corporate discussions more interactive, efficient, and impactful.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-executive-boardroom-displays.webp",
  },
  {
    id: 3,
    title: "Conference & Huddle Rooms – Seamless Team Collaboration",
    altText:
      "COLTEC Inertia LED Video Wall in Control Rooms – Real-time data visualization for mission-critical monitoring.",
    description:
      "Modern conference and huddle rooms benefit from interactive 4K commercial displays, allowing teams to annotate, brainstorm, and collaborate in real-time. Wireless screen sharing eliminates cable clutter, while multi-user engagement supports simultaneous presentations. AI-powered noise cancellation and an 8-mic array ensure crystal-clear communication, making COLTEC displays the perfect choice for productive meetings.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-conference-huddle-room-displays.webp",
  },
  {
    id: 4,
    title: "Control Rooms – Real-Time Monitoring & Decision Making",
    altText:
      "COLTEC Inertia LED Video Wall in Boardrooms – Enhancing corporate presentations & meetings.",
    description:
      "Control rooms and network operations centers (NOC) require commercial-grade video walls for real-time data visualization and security monitoring. High brightness, 24/7 reliability, and zero-latency performance ensure seamless operations. Remote access and cloud integration allow IT teams to manage screens efficiently, improving situational awareness and rapid decision-making in critical environments.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-control-room-displays.webp",
  },
  {
    id: 5,
    title:
      "Indoor Corporate Digital Signage – Dynamic Communication & Branding",
    altText:
      "COLTEC Inertia LED Video Wall in Education & Training Centers – Interactive learning and digital teaching solutions.",
    description:
      "Corporate offices use commercial signage displays for employee engagement, visitor communication, and brand storytelling. Live updates, announcements, and real-time dashboards enhance information flow. Cloud-based content management enables remote control across multiple locations. Automated scheduling and dynamic messaging ensure that company updates are always relevant, improving internal communication and corporate branding.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-indoor-corporate-digital-signage.webp",
  },
];

const relatedCases = [
  {
    id: 1,
    imageUrl:
      "https://content.qeeb.in/coltec/products/commercial-display/commercial-display-for-reception-area.webp",
    altText:
      "LED Video Wall Use Case in Corporate Reception – Dynamic branding and engaging digital signage.",
    buttonText: "Reception Area",
  },
  {
    id: 2,
    imageUrl:
      "https://content.qeeb.in/coltec/products/commercial-display/commercial-display-use-for-digital-signage-area.webp",
    altText:
      "LED Video Wall Use Case in News Room – Real-time broadcast visuals and live updates.",
    buttonText: "Digital Signage",
  },
  {
    id: 3,
    imageUrl:
      "https://content.qeeb.in/coltec/products/commercial-display/commercial-display-use-for-large-board-room.webp",
    altText:
      "LED Video Wall Use Case in Boardroom – Enhancing corporate presentations and discussions.",
    buttonText: "Board Room",
  },
];

const Vista = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileCurrentIndex, setMobileCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const goToSlide = (slideIndex) => {
    setCurrentIndex(slideIndex);
  };

  // Mobile slider navigation for Related Cases
  const nextSlide = () => {
    setMobileCurrentIndex((prevIndex) => (prevIndex + 1) % relatedCases.length);
  };

  const prevSlide = () => {
    setMobileCurrentIndex(
      (prevIndex) => (prevIndex - 1 + relatedCases.length) % relatedCases.length
    );
  };

  // Automatically change slide every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <div>
        <SEO
          title="Vista Series | Commercial Digital Signage Displays | COLTEC GLOBAL"
          description="Discover the Vista Series from COLTEC GLOBAL – high-performance commercial displays designed for crystal-clear signage, robust 24/7 operation, and modern digital communication. Ideal for retail, corporate, and public environments."
          ogTitle="Vista Series – Commercial Digital Signage Displays | COLTEC GLOBAL"
          keywords="Vista Series, Commercial Displays, Digital Signage, COLTEC GLOBAL, Commercial Display Solutions, 24/7 Displays, Retail Display Screens, Corporate Signage, Large Format Displays, LED Displays, TV for boardroom, TV for office, TV for conference room, Enterprise Display"
          ogDescription="Experience stunning visuals and unmatched performance with the Vista Series by COLTEC GLOBAL. Tailored for digital signage in retail, corporate, and public environments."
          ogImage="https://content.qeeb.in/coltec/products/commercial-display/coltec-commercial-display-vista-banner-img.webp"
          ogUrl="https://coltec-global.com/products/commercial-display/vista-series"
          canonicalUrl="https://coltec-global.com/products/commercial-display/vista-series"
          ogImageAlt="COLTEC Vista Series Commercial Digital Display"
          ogSiteName="Coltec Global"
          ogType="website"
          twitterSite="https://x.com/ColtecGlobal" // Twitter username associated with the site
          twitterTitle="Vista Series | Commercial Digital Signage Displays | COLTEC GLOBAL"
          twitterDescription="Discover the Vista Series from COLTEC GLOBAL – high-performance commercial displays designed for crystal-clear signage, robust 24/7 operation, and modern digital communication. Ideal for retail, corporate, and public environments."
          twitterImage="https://content.qeeb.in/coltec/products/commercial-display/coltec-commercial-display-vista-banner-img.webp"
          twitterCard="summary" // Using a large image card for enhanced visual display
          geoRegion="IN"
        />
        {/* JSON‑LD Structured Data with Publisher & Address Details */}
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(
              {
                "@context": "https://schema.org",
                "@graph": [
                  // Organization details
                  {
                    "@type": "Organization",
                    "@id": "https://coltec-global.com/#organization",
                    name: "COLTEC Global",
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: "4.8",
                      reviewCount: "350",
                    },
                    description:
                      "COLTEC Global is a leading manufacturer of Visual Interactive Display Technology, delivering innovative solutions for Education, Corporate, and Retail applications.",
                    url: "https://coltec-global.com",
                    logo: {
                      "@type": "ImageObject",
                      url: "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    },
                    address: [
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                        addressLocality: "Hyderabad",
                        addressRegion: "Telangana",
                        postalCode: "500030",
                        addressCountry: "IN",
                      },
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "COLTEC GLOBAL #41-14-8/2, Opp Mouli Towers, Near Jyothi Convention Hall,Benz circle",
                        addressLocality: "Vijayawada",
                        addressRegion: "Andhra Pradesh",
                        postalCode: "520 010",
                        addressCountry: "IN",
                      },
                    ],
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                    email: "sales@coltec-global.com",
                    contactPoint: [
                      {
                        "@type": "ContactPoint",
                        telephone: "+91 1800 309 8822",
                        contactType: "customer support",
                      },
                    ],
                  },
                  // WebSite details
                  {
                    "@type": "WebSite",
                    "@id": "https://coltec-global.com/#website",
                    url: "https://coltec-global.com",
                    name: "COLTEC Global",
                    publisher: {
                      "@id": "https://coltec-global.com/#organization",
                    },
                    potentialAction: {
                      "@type": "SearchAction",
                      target:
                        "https://coltec-global.com/?s={search_term_string}",
                      "query-input": "required name=search_term_string",
                    },
                    inLanguage: "en",
                  },
                  {
                    "@type": "Product",
                    name: "COLTEC Vista Commercial Display",
                    url: "https://coltec-global.com/products/commercial-display/vista-series",
                    description:
                      "Step into the future of commercial displays with COLTEC Vista – a cutting-edge display solution designed to deliver crystal-clear visuals, seamless connectivity, and unmatched reliability. Whether for retail, corporate, hospitality, or control centers, COLTEC Vista transforms any space with its ultra-HD resolution, sleek design, and 24/7 performance.",
                    brand: {
                      "@type": "Brand",
                      name: "COLTEC Vista Commercial Display",
                      url: "https://coltec-global.com/products/commercial-display/vista-series",
                      image:
                        "https://content.qeeb.in/coltec/products/commercial-display/coltec-commercial-display-vista-banner-img.webp",
                    },
                    offers: {
                      "@type": "Offer",
                      offerCount: 5,
                      lowPrice: "35,000",
                      highPrice: "6,20,000",
                      priceCurrency: "INR",
                      availability: "https://schema.org/InStock",
                    },
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: 4.4,
                      reviewCount: 100,
                    },
                    review: [
                      {
                        "@type": "Review",
                        headline: "Bright, Engaging Screen That Captures Attention",
                        reviewRating: {
                          "@type": "Rating",
                          ratingValue: 4,
                          bestRating: 5,
                        },
                        author: {
                          "@type": "Person",
                          name: "Srikanth",
                        },
                        datePublished: "2024-02-10T12:00:00+05:30",
                      },
                      {
                        "@type": "Review",
                        headline: "Reliable, Eye-Catching Display for High-Traffic Areas",
                        reviewRating: {
                          "@type": "Rating",
                          ratingValue: 4,
                          bestRating: 5,
                        },
                        author: {
                          "@type": "Person",
                          name: "Mohammad Sameer",
                        },
                        datePublished: "2024-01-15T10:30:00+05:30",
                      },
                    ],
                    image: [
                      {
                        "@type": "ImageObject",
                        name: "Indoor Commercial Displays for Retail Showrooms & Malls",
                        url: "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-display-travel-bag-store.webp",
                        description:
                          "Retail showrooms and shopping malls use high-resolution commercial displays for dynamic advertising and customer engagement. Ultra-HD video walls showcase promotions, product highlights, and interactive content to attract shoppers. AI-powered analytics track customer engagement, while interactive touchscreen kiosks allow personalized browsing, enhancing brand visibility, customer experience, and in-store sales.",
                      },
                      {
                        "@type": "ImageObject",
                        name: "Executive Boardrooms – High-Impact Business Presentations",
                        url: "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-executive-boardroom-displays.webp",
                        description:
                          "Executive boardrooms demand high-performance commercial displays for seamless presentations, financial discussions, and investor meetings. Ultra-HD resolution ensures sharp visuals, while wireless content sharing and multi-screen support enhance collaboration. Built-in video conferencing compatibility with Zoom and Microsoft Teams facilitates hybrid meetings, making corporate discussions more interactive, efficient, and impactful.",
                      },
                      {
                        "@type": "ImageObject",
                        name: "Conference & Huddle Rooms – Seamless Team Collaboration",
                        url: "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-conference-huddle-room-displays.webp",
                        description:
                          "Modern conference and huddle rooms benefit from interactive 4K commercial displays, allowing teams to annotate, brainstorm, and collaborate in real-time. Wireless screen sharing eliminates cable clutter, while multi-user engagement supports simultaneous presentations. AI-powered noise cancellation and an 8-mic array ensure crystal-clear communication, making COLTEC displays the perfect choice for productive meetings.",
                      },
                      {
                        "@type": "ImageObject",
                        name: "Control Rooms – Real-Time Monitoring & Decision Making",
                        url: "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-control-room-displays.webp",
                        description:
                          "Control rooms and network operations centers (NOC) require commercial-grade video walls for real-time data visualization and security monitoring. High brightness, 24/7 reliability, and zero-latency performance ensure seamless operations. Remote access and cloud integration allow IT teams to manage screens efficiently, improving situational awareness and rapid decision-making in critical environments.",
                      },
                      {
                        "@type": "ImageObject",
                        name: "Indoor Corporate Digital Signage – Dynamic Communication & Branding",
                        url: "https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-indoor-corporate-digital-signage.webp",
                        description:
                          "Corporate offices use commercial signage displays for employee engagement, visitor communication, and brand storytelling. Live updates, announcements, and real-time dashboards enhance information flow. Cloud-based content management enables remote control across multiple locations. Automated scheduling and dynamic messaging ensure that company updates are always relevant, improving internal communication and corporate branding.",
                      },
                    ],
                  },
                  // Breadcrumb for Inspire Series page
                  {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: "https://coltec-global.com",
                      },
                      {
                        "@type": "ListItem",
                        position: 2,
                        name: "Coltec Vista Series",
                        item: "https://coltec-global.com/products/commercial-display/vista-series",
                      },
                    ],
                  },
                  {
                    "@type": "LocalBusiness",
                    "@id": "https://coltec-global.com/#localbusiness",
                    name: "COLTEC Global",
                    url: "https://coltec-global.com",
                    image:
                      "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    address: {
                      "@type": "PostalAddress",
                      streetAddress:
                        "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                      addressLocality: "Hyderabad",
                      addressRegion: "Telangana",
                      postalCode: "500030",
                      addressCountry: "IN",
                    },
                    telephone: "+91 1800 309 8822",
                    openingHours: "Mon-Sat 09:00-6:00",
                    geo: {
                      "@type": "GeoCoordinates",
                      latitude: "17.3297791",
                      longitude: "78.4381149",
                    },
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                  },
                ],
              },
              null,
              2
            )}
          </script>
        </Helmet>
        <Header />
        <div className="relative">
          <Banner
            imgSrc="https://content.qeeb.in/coltec/products/commercial-display/coltec-commercial-display-vista-banner-img.webp"
            mobileImgSrc="https://content.qeeb.in/coltec/products/commercial-display/coltec-commercial-display-vista-mobile.webp"
            altText="COLTEC Vista Commercial Display at Corporate Reception for Branding & Visitor Information."
            headerText="VISTA SERIES"
            bannerHeight="md:h-[800px] h-[300px]"
            imgHeight="h-[300px] lg:h-auto w-full"
            heading="Commercial Display Series - Where Innovation Meets Visibility"
            headClasses="hidden md:block max-w-md text-[18px] font-semibold tracking-tight leading-6 text-white md:text-xl drop-shadow-lg md:mt-3"
            opacityClasses="opacity-layer absolute inset-0 w-full h-full bg-neutral-900/5 md:bg-neutral-900/10 z-0"
            positionClasses="absolute md:top-[40%] bottom-10 left-0 lg:left-0 lg:right-0 text-center"
            headerTextClasses="hidden md:block xl:text-[50px] xl:max-w-[400px] md:text-[44px] text-[24px] font-bold not-italic text-[#FFD167] leading-1 md:leading-[65px]"
            paragraphTextClasses="hidden md:block md:text-[13px] text-[11px] not-italic font-normal mt-1 md:mt-4 text-white max-w-[410px]"
            paragraphText="Step into the future of commercial displays with COLTEC Vista – a cutting-edge display solution designed to deliver crystal-clear visuals, seamless connectivity, and unmatched reliability. Whether for retail, corporate, hospitality, or control centers, COLTEC Vista transforms any space with its ultra-HD resolution, sleek design, and 24/7 performance."
            paragraphTextThree="Built for maximum impact, these commercial displays enhance branding, presentations, and digital engagement like never before."
            paragraphTextThreeClasses="hidden md:block md:max-w-[410px] md:text-[13px] text-[11px] font-normal text-white"
          />
          <div className="md:hidden block px-6 py-8">
            <div className="bg-neutral-900/60 absolute bottom-[295px] left-0 right-0 px-5 py-4">
              <h2 className="relative justify-start text-white mb-2 text-[36px] font-bold uppercase leading-[34.56px]">
                VISTA SERIES
              </h2>
              <p className="relative justify-start text-white text-base font-medium font-['Poppins'] leading-snug">
                Where Innovation Meets Visibilty
              </p>
            </div>
            <p className="max-w-[373px] relative justify-start text-black text-sm mb-3 font-normal font-['Poppins'] leading-[20px]">
              Step into the future of commercial displays with COLTEC Vista – a
              cutting-edge display solution designed to deliver crystal-clear
              visuals, seamless connectivity, and unmatched reliability. Whether
              for retail, corporate, hospitality, or control centers, COLTEC
              Vista transforms any space with its ultra-HD resolution, sleek
              design, and 24/7 performance.
            </p>
            <p className="max-w-[373px] relative justify-start text-black text-sm font-normal font-['Poppins'] leading-[20px]">
              Built for maximum impact, these commercial displays enhance
              branding, presentations, and digital engagement like never before.
            </p>
          </div>
        </div>
        <div className="md:py-10 py-8">
          <div className="px-6 lg:px-8 max-w-7xl mx-auto text-black">
            <div>
              <h4 className="md:text-center md:mb-10 mb-5 hidden">
                <span class="text-white md:text-4xl text-2xl font-semibold font-['Poppins'] leading-[57.60px]">
                  Key{" "}
                </span>
                <span class="text-scenarios md:text-4xl text-2xl font-semibold font-['Poppins'] leading-[57.60px]">
                  Features
                </span>
              </h4>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="relative">
                  <div className="flex items-center flex-col md:h-[60px] justify-between md:pr-8">
                    <div>
                      <img
                        alt="4K Resolution"
                        src="https://content.qeeb.in/coltec/products/commercial-display/4k-resolution.svg"
                        className="h-8 w-auto md:mb-0 mb-2"
                      />
                    </div>
                    <p className="text-sm font-medium">4K Resolution</p>
                  </div>
                  <div className="md:block hidden absolute right-0 top-0">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/commercial-display/line-feature.svg"
                    />
                  </div>
                </div>
                <div className="relative">
                  <div className="flex items-center flex-col md:h-[60px] justify-between md:pr-8">
                    <div>
                      <img
                        alt="True Color Accuracy"
                        src="https://content.qeeb.in/coltec/products/commercial-display/true-color-accuracy.svg"
                        className="h-8 md:mb-0 mb-2 w-auto"
                      />
                    </div>
                    <p className="text-sm font-medium">True Color Accuracy</p>
                  </div>
                  <div className="md:block hidden absolute right-0 top-0">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/commercial-display/line-feature.svg"
                    />
                  </div>
                </div>
                <div className="relative">
                  <div className="flex items-center flex-col md:h-[60px] justify-between md:pr-8">
                    <div>
                      <img
                        alt="Intelligent Backlight Control"
                        src="https://content.qeeb.in/coltec/products/commercial-display/intelligent-control.svg"
                        className="h-8 mb-2 md:mb-0 w-auto"
                      />
                    </div>
                    <p className="text-sm text-center font-medium">
                      Intelligent Backlight Control
                    </p>
                  </div>
                  <div className="hidden md:block absolute right-0 top-0">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/commercial-display/line-feature.svg"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center flex-col md:h-[60px] justify-between">
                    <div>
                      <img
                        alt="Cloud-Based Remote Management"
                        src="https://content.qeeb.in/coltec/products/commercial-display/cloud-based-remote.svg"
                        className="h-8 mb-2 md:mb-0 w-auto"
                      />
                    </div>
                    <p className="text-sm text-center font-medium">
                      Cloud-Based Remote Management
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                <div className="relative">
                  <div className="flex items-center text-center flex-col md:h-[60px] justify-between md:pr-8">
                    <div>
                      <img
                        alt="24/7 Reliability & Durability"
                        src="https://content.qeeb.in/coltec/products/commercial-display/reliability-durability.svg"
                        className="h-8 w-auto mb-2"
                      />
                    </div>
                    <p className="text-sm font-medium">
                      24/7 Reliability & Durability
                    </p>
                  </div>
                  <div className="md:block hidden absolute right-0 top-0">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/commercial-display/line-feature.svg"
                    />
                  </div>
                </div>
                <div className="relative">
                  <div className="flex items-center flex-col md:h-[60px] justify-between md:pr-8">
                    <div>
                      <img
                        alt="Versatile Connectivity"
                        src="https://content.qeeb.in/coltec/products/commercial-display/versatile-connectivity.svg"
                        className="h-8 md:mb-0 mb-2 w-auto"
                      />
                    </div>
                    <p className="text-sm font-medium">
                      Versatile Connectivity
                    </p>
                  </div>
                  <div className="md:block hidden absolute right-0 top-0">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/commercial-display/line-feature.svg"
                    />
                  </div>
                </div>
                <div className="relative">
                  <div className="flex items-center flex-col md:h-[60px] justify-between md:pr-8">
                    <div>
                      <img
                        alt="16:9 Cabinet Aspect Ratio"
                        src="https://content.qeeb.in/coltec/products/active-led/cabinet-aspect-ratio.svg"
                        className="h-7 md:h-6 mb-2 md:mb-0 w-auto"
                      />
                    </div>
                    <p className="text-sm text-center font-medium">
                      16:9 Aspect Ratio
                    </p>
                  </div>
                  <div className="hidden md:block absolute right-0 top-0">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/commercial-display/line-feature.svg"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center flex-col md:h-[60px] justify-between">
                    <div>
                      <img
                        alt="Integrated Module Design"
                        src="https://content.qeeb.in/coltec/products/active-led/integrated-module-design.svg"
                        className="h-7 md:h-6 mb-2 md:mb-0 w-auto"
                      />
                    </div>
                    <p className="text-sm text-center font-medium">
                      Integrated Module Design
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <img
            className="lg:h-[550px] w-full"
            alt="COLTEC Vista Digital Display in a Jewelry Store Showcasing Luxury Collections"
            src="https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-commercial-display-shopping-mall.webp"
          />
          <div
            className="md:pt-8 md:pb-16 pt-8 pb-14"
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="300"
          >
            <div className="lg:w-[567px] px-5 md:px-0 mx-auto md:text-center">
              <h2 class="text-black md:text-[28px] text-[22px] font-medium font-['Poppins'] leading-[33.60px]">
                Stunning Visual Clarity with High-Resolution{" "}
                <span>Performance</span>
              </h2>
            </div>
            <div className="md:w-[750px] px-5 md:px-0 mx-auto md:text-center md:pt-6 pt-4">
              <h3 class="text-black text-sm font-normal font-['Poppins'] leading-normal">
                Experience crystal-clear visuals with COLTEC Vista Commercial
                Display, delivering ultra-HD 4K/8K resolution for sharp, vibrant
                imagery. With high brightness and superior contrast, every
                detail remains crisp and clear, even in brightly lit
                environments. The wide viewing angles ensure consistent image
                quality, making it the perfect choice for retail spaces,
                corporate settings, and control rooms.
              </h3>
            </div>
          </div>
        </div>

        <div>
          <img
            className="lg:h-[500px] object-cover w-full"
            alt="High-Definition COLTEC Vista Display Enhancing Retail Store Customer Engagement."
            src="https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-commercial-display-in-malls.webp"
          />
          <div
            className="md:pt-8 md:pb-16 pt-8 pb-14"
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="300"
          >
            <div className="lg:w-[350px] mx-auto px-5 md:px-0 md:text-center">
              <h2 class="text-black md:text-[28px] text-[22px] font-medium font-['Poppins'] leading-[33.60px]">
                Smart Connectivity & Seamless Integration{" "}
              </h2>
            </div>
            <div className="md:w-[780px] mx-auto md:text-center px-5 md:px-0 md:pt-6 pt-4">
              <h3 class="text-black text-sm font-normal font-['Poppins'] leading-normal">
                Seamlessly connect and display content with multiple input
                options, including HDMI, DisplayPort, USB, and wireless sharing.
                COLTEC Vista Commercial Screens are designed for digital
                signage, interactive presentations, and real-time content
                streaming, making them ideal for retail stores, boardrooms, and
                public spaces. With cloud-enabled and IoT-ready capabilities,
                managing content remotely and in real-time has never been
                easier.
              </h3>
            </div>
          </div>
        </div>

        <div>
          <img
            className="lg:h-[650px] w-full object-center"
            alt="COLTEC Inertia LED Video Wall for Retail – Captivating shoppers with high-quality digital displays."
            src="https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-commercial-display-in-museum.webp"
          />
          <div
            className="md:pt-8 md:pb-16 pt-8 pb-14"
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="300"
          >
            <div className="lg:w-[400px] mx-auto px-5 md:px-0 lg:text-center">
              <h2 class="text-black md:text-[28px] text-[22px] font-medium font-['Poppins'] leading-[33.60px]">
                Durable, Energy-Efficient & 24/7 Operation
              </h2>
            </div>
            <div className="md:w-[900px] mx-auto md:text-center px-5 md:px-0 pt-4 md:pt-6">
              <h3 class="text-black text-sm font-normal font-['Poppins'] leading-normal">
                Designed for continuous use, COLTEC Vista Commercial Display
                ensures reliable 24/7 performance, making it the ultimate choice
                for high-traffic environments. Featuring energy-efficient
                technology, it reduces power consumption while maintaining
                exceptional brightness and performance. Built with
                industrial-grade components, these commercial screens offer
                long-lasting durability, ensuring an excellent return on
                investment for businesses that demand reliability and
                performance.
              </h3>
            </div>
          </div>
        </div>

        <div className="md:pb-16">
          <div className="px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="text-center">
              <span class="text-black lg:text-4xl text-2xl font-semibold font-['Poppins'] md:leading-[57.60px]">
                Application{" "}
              </span>
              <span class="text-scenarios lg:text-4xl text-2xl font-semibold font-['Poppins'] md:leading-[57.60px]">
                Scenarios
              </span>
            </div>
            <div className="w-full md:pt-10 pt-8">
              {/* Slider Container */}
              <div className="relative overflow-hidden md:h-80 h-[600px]">
                {slides.map((slide, index) => (
                  <div
                    key={slide.id}
                    className={`
              absolute inset-0 transition-all duration-700 
              ${index === currentIndex ? "opacity-100" : "opacity-0"}
            `}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-16 gap-4">
                      <div>
                        <img
                          src={slide.imageUrl}
                          alt={slide.altText}
                          className="w-full md:h-[300px] object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex flex-col justify-between">
                        <div>
                          <h2 className="md:text-2xl lg:w-[500px] text-xl text-black md:mb-8 mb-4 font-semibold">
                            {slide.title}
                          </h2>
                          <p className="md:text-[14px] text-sm leading-6 text-black font-normal">
                            {slide.description}
                          </p>
                        </div>
                        {/* Custom Pagination with Outer Circle and Connecting Line */}
                        <div className="flex items-center mt-5 md:mt-0">
                          {slides.map((_, index) => (
                            <React.Fragment key={index}>
                              <button
                                onClick={() => goToSlide(index)}
                                className="flex flex-col items-center rounded-full focus:outline-none"
                              >
                                {/* Outer circle */}
                                <div
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${
                                    index === currentIndex
                                      ? "border-linergrd"
                                      : "border-gray-800"
                                  }`}
                                >
                                  {/* Inner circle if active */}
                                  {index === currentIndex && (
                                    <div className="w-2 h-2 rounded-full gradient-background"></div>
                                  )}
                                </div>
                              </button>
                              {/* Connecting line between dots (except after the last dot) */}
                              {index !== slides.length - 1 && (
                                <div className="w-14 h-0.5 bg-gray-800 mx-2 rounded-xl"></div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-14">
            <div className="lg:hidden block">
              <img
                className="lg:h-[360px] w-full rounded-lg mb-6"
                alt="COLTEC Vista Digital Display"
                src="https://content.qeeb.in/coltec/products/commercial-display/commercial-display-reliablity-technology.webp"
              />
            </div>
            <div>
              <p className="text-black pb-8 text-[15px] font-normal font-['Poppins'] leading-normal">
                The COLTEC Vista Commercial Display Series is engineered with
                cutting-edge display technology to deliver unmatched visual
                clarity, seamless integration, and enterprise-grade reliability.
                Featuring Ultra-HD 4K resolution, high brightness, and
                anti-glare technology, it ensures crisp, vibrant visuals in any
                lighting condition. Zero-latency screen processing, high refresh
                rates, and advanced IPS panel technology provide smooth,
                flicker-free performance.
              </p>
              <p className="text-black text-[15px] font-normal font-['Poppins'] leading-normal">
                With multiple connectivity options including HDMI, USB-C,
                DisplayPort, and wireless screen sharing, COLTEC Vista
                integrates effortlessly into business ecosystems. Its
                cloud-based remote management system enables centralized content
                control, while energy-efficient components ensure 24/7
                durability with minimal power consumption.
              </p>
              <div className="md:pt-6 md:pb-0 pt-8 flex justify-center md:justify-start">
                <button
                  onClick={openModal}
                  className="px-5 flex items-center hover:bg-gray-800 relative text-base font-semibold h-11 bg-black text-white rounded-[22px] border"
                >
                  Request Consultation
                  <img
                    alt="arrow-right"
                    src="https://content.qeeb.in/coltec/chevron-right.svg"
                    class="chevron-right ml-2 invert w-[20px]"
                  ></img>
                </button>
              </div>
            </div>
            <div className="lg:block hidden">
              <img
                className="lg:h-[360px] w-full rounded-lg"
                alt="COLTEC Vista Digital Display"
                src="https://content.qeeb.in/coltec/products/commercial-display/commercial-display-reliablity-technology.webp"
              />
            </div>
          </div>
        </div>
        <div className="md:pt-16 md:pb-16 py-8">
          <div className="px-5 lg:px-8 max-w-7xl mx-auto">
            <div className="md:w-[755px] mx-auto md:text-center md:mb-8 mb-4">
              <span class="text-black lg:text-4xl text-[25px] font-semibold font-['Poppins'] lg:leading-[50.60px]">
                COLTEC{" "}
              </span>
              <span class="text-[#6dc7ff] lg:text-4xl text-[25px] font-semibold font-['Poppins'] lg:leading-[50.60px]">
                Where Cutting-Edge Technology
              </span>
              <span class="text-black lg:text-4xl text-[25px] font-semibold font-['Poppins'] lg:leading-[50.60px]">
                {" "}
                Meets Uncompromised Clarity
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-16">
              <div className="lg:pt-6">
                <p className="md:w-[480px] text-black text-sm font-normal font-['Poppins'] leading-normal mb-6">
                  COLTEC Vista Commercial Display Series is built on proprietary
                  display and processing technology, ensuring superior
                  performance, longevity, and seamless integration. Unlike
                  standard commercial screens, COLTEC’s proprietary image
                  processing engine enhances color accuracy, contrast, and
                  motion clarity for ultra-smooth visuals.
                </p>

                <p className="md:w-[480px] text-black text-sm font-normal font-['Poppins'] leading-normal mb-6">
                  Our zero-latency touch response and smart AI-driven upscaling
                  provide lifelike image precision. Advanced thermal management
                  extends display lifespan, while intelligent backlight control
                  ensures energy efficiency without compromising brightness.
                  Designed for enterprise-grade applications, COLTEC Vista’s
                  custom-engineered chipset and firmware optimize real-time
                  content processing, setting a new benchmark for commercial
                  display excellence.
                </p>
              </div>
              <div className="relative">
                <img
                  alt="LED Video Wall Customizable in Multiple Sizes – Tailored solutions for different spaces."
                  src="https://content.qeeb.in/coltec/products/commercial-display/coltec-vista-multipule-sizes-displays.webp"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="md:pb-16 pb-8">
          <div className="px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <span className="text-black lg:text-4xl text-2xl font-semibold font-['Poppins'] md:leading-[57.60px]">
                Related{" "}
              </span>
              <span className="text-scenarios lg:text-4xl text-2xl font-semibold font-['Poppins'] md:leading-[57.60px]">
                Cases
              </span>
            </div>
            {/* Desktop Grid View (visible on md and larger) */}
            <div className="hidden md:grid lg:grid-cols-3 grid-cols-1 gap-10">
              {relatedCases.map((item) => (
                <div key={item.id} className="text-center">
                  <img
                    alt={item.altText}
                    className="w-[448px] rounded-sm mb-6"
                    src={item.imageUrl}
                  />
                  <button className="bg-[#3089ca] text-white px-5 py-2 rounded-lg border border-[#3089ca] backdrop-blur-lg">
                    {item.buttonText}
                  </button>
                </div>
              ))}
            </div>
            {/* Mobile Slider View (visible only on mobile) */}
            <div className="md:hidden relative">
              <div className="overflow-hidden">
                {relatedCases.map((item, index) => (
                  <div
                    key={item.id}
                    className={`transition-transform duration-500 ${
                      index === mobileCurrentIndex ? "block" : "hidden"
                    }`}
                  >
                    <div className="text-center">
                      <img
                        alt={item.altText}
                        className="w-full rounded-sm mb-6"
                        src={item.imageUrl}
                      />
                      <button className="bg-[#3089ca] text-white px-5 py-2 rounded-lg border border-[#3089ca] backdrop-blur-lg">
                        {item.buttonText}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Navigation Buttons */}
              <button
                onClick={prevSlide}
                className="absolute left-[-15px] top-32 transform -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full"
              >
                <FaChevronLeft size={20} />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-[-15px] top-32 transform -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full"
              >
                <FaChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
        <div className="md:pb-16 pb-12">
          <div className="px-6 lg:px-8 max-w-7xl mx-auto text-black inertia-ps">
            <div className="text-center md:mb-8 mb-4">
              <span class="text-black md:text-4xl text-2xl font-semibold font-['Poppins'] leading-[57.60px]">
                Product{" "}
              </span>
              <span class="text-scenarios md:text-4xl text-2xl font-semibold font-['Poppins'] leading-[57.60px]">
                Specifications
              </span>
            </div>
            <div className="relative">
              <ProductSpecifications
                specs={vistaProductSpecs}
                gridClasses="xl:grid-cols-7"
              />
            </div>
          </div>
        </div>
        <div className="w-full relative h-[156px] bg-gradient-to-l from-[#6d91ff] to-[#2eaccf]">
          <div className="px-6 py-8 sm:px-6 lg:py-14 lg:px-8 max-w-7xl mx-auto">
            <div className="flex md:flex-row flex-col justify-start items-start md:justify-between md:items-center">
              <h2 className="lg:text-[30px] text-[19px] font-medium leading-[1.3] text-white md:max-w-3xl">
                Upgrade to Next-Level Visual Brilliance
              </h2>
              <div className="flex items-center justify-center gap-x-6 mt-4 md:mt-0">
                <button
                  onClick={openModal}
                  className="flex items-center rounded-md bg-white lg:px-6 px-6 py-2 text-xs md:text-sm font-medium text-[#0A78B9] shadow-sm hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  Get a Quote Now
                  <img
                    alt="right-arrow"
                    src="https://content.qeeb.in/coltec/blue-arrow.svg"
                    className="h-2 pl-2"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <ScrollToTopButton />
        <WhatsAppMsg />
        {/* Modal popup */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div
                className="fixed inset-0 transition-opacity"
                onClick={closeModal}
              >
                <div className="absolute inset-0 bg-black opacity-50"></div>
              </div>
              <div className="bg-[#f1f1f1] rounded-lg overflow-hidden shadow-xl transform transition-all max-w-[770px] w-full z-10">
                <div className="flex items-center justify-between p-4 bg-white">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Contact Form
                  </h3>
                  <div>
                    <button onClick={closeModal}>
                      <img
                        alt="close-icon"
                        className="w-6"
                        src="https://content.qeeb.in/coltec/close-icon.svg"
                      />
                    </button>
                  </div>
                </div>
                <div className="modal-iframe">
                  <iframe
                    src="/modal-form.html"
                    title="outer-iframe"
                    className="w-full h-full search-iframe"
                    style={{ border: "none" }}
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Vista;
